import { type ChangeEvent, type Dispatch, memo, type SetStateAction } from "react";
import "Components/ConfigLayout/Event/ActionSettings/Audio/Audio.less";
import {
	Button, ButtonTypes, Checkbox, ConfirmModal, Icon, IconSize, IconWeight, Input, type SelectOption, Popover, Select
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { ActionState, AudioFile } from "Interfaces";
import { Link } from "react-router-dom";
import { defaultAudioFiles } from "Data/Objects/Event";
import AddAudioModal from "Components/ConfigLayout/Event/ActionSettings/AddAudioModal";

const audioOptions: SelectOption[] = defaultAudioFiles.map((file) => ({
	label: file,
	value: file
}));

type AudioProps = {
	localActionState: [ ActionState, Dispatch<SetStateAction<ActionState>> ];
};

function Audio(props: AudioProps) {
	const { localActionState } = props;
	const [ localActionSettings, setLocalActionSettings ] = localActionState;
	const link = (
		<div>
			Go to <Link to="/setup/system/schedule">Schedule Preset</Link> to create a new preset.
		</div>
	);

	const handleAlarmAudioSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.alarmEvent.audio = value;
		}));
	};

	const handleMotionAudioSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.motionEvent.audio = value;
		}));
	};

	const handleTamperAudioSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.tamperEvent.audio = value;
		}));
	};

	const handleAlarmCountUpdate = (count: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.alarmEvent.count = +count;
		}));
	};

	const handleMotionCountUpdate = (count: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.motionEvent.count = +count;
		}));
	};

	const handleTamperCountUpdate = (count: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.tamperEvent.count = +count;
		}));
	};

	const handleAiAnalyticsCountUpdate = (count: string) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.aiAnalytics = +count;
		}));
	};

	const handleScheduleChange = (scheduleName: string) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.schedule[ scheduleName ] = event.currentTarget.checked;
		}));
	};

	const deleteAudio = (index: number) => () => {
		setLocalActionSettings(produce(localActionSettings, (draft) => {
			draft.audio.audioFiles.splice(index, 1);
		}));
	};

	const renderSchedule = (schedule: [ string, boolean ]) => {
		const [ scheduleName, checked ] = schedule;

		return (
			<Checkbox checked={ checked } key={ scheduleName } onChange={ handleScheduleChange(scheduleName) }>
				<span>{ scheduleName }</span>
			</Checkbox>
		);
	};

	const renderAudioListItem = (audioFile: AudioFile, index: number) => {
		const disabledClass = audioFile.fixed ? "list-icon disabled" : "list-icon";
		const editIcon = audioFile.fixed
			? (
				<Icon
					className={ disabledClass }
					name="pencil"
					iconWeight={ IconWeight.SOLID }
					size={ IconSize.SMALLEST }
				/>
			)
			: (
				<AddAudioModal localActionState={ localActionState } index={ index }>
					<Icon
						className={ disabledClass }
						name="pencil"
						iconWeight={ IconWeight.SOLID }
						size={ IconSize.SMALLEST }
					/>
				</AddAudioModal>
			);

		const deleteIcon = audioFile.fixed
			? (
				<Icon
					className={ disabledClass }
					name="trash"
					iconWeight={ IconWeight.SOLID }
					size={ IconSize.SMALLEST }
				/>
			)
			: (
				<ConfirmModal
					cancelButton={ { text: "Cancel", type: ButtonTypes.DANGER } }
					modalContent="Are you sure you want delete this audio?"
					title="Delete Audio"
					okButton={ { text: "Confirm", onClick: deleteAudio(index), type: ButtonTypes.TERTIARY } }
				>
					<Icon
						className={ disabledClass }
						name="trash"
						iconWeight={ IconWeight.SOLID }
						size={ IconSize.SMALLEST }
					/>
				</ConfirmModal>
			);

		return (
			<div className="audio-list-item" key={ index }>
				<Icon
					className="list-icon"
					name="play"
					iconWeight={ IconWeight.SOLID }
					size={ IconSize.SMALLEST }
				/>
				<span>{ audioFile.name }</span>
				<Icon
					className="list-icon"
					name="download"
					iconWeight={ IconWeight.SOLID }
					size={ IconSize.SMALLEST }
				/>
				{ editIcon }
				{ deleteIcon }
			</div>
		);
	};

	return (
		<div className="audio">
			<div className="column">
				<AddAudioModal localActionState={ localActionState }>
					<Button className="add-audio" type={ ButtonTypes.TERTIARY }>Add Audio</Button>
				</AddAudioModal>
				<div className="audio-list">
					{ localActionSettings.audio.audioFiles.map(renderAudioListItem) }
				</div>
			</div>
			<div className="column">
				<div className="form-row header-row">
					<span>Event Type</span>
					<span>Audio File</span>
					<span>Count</span>
				</div>
				<div className="form-row">
					<span>Alarm Event</span>
					<Select
						allowClear={ false }
						onSelect={ handleAlarmAudioSelect }
						options={ audioOptions }
						value={ localActionSettings.audio.alarmEvent.audio }
					/>
					<Input
						hideCharMessage={ true }
						max={ 10 }
						min={ 1 }
						onUpdate={ handleAlarmCountUpdate }
						type="number"
						value={ localActionSettings.audio.alarmEvent.count }
					/>
				</div>
				<div className="form-row">
					<span>Motion Event</span>
					<Select
						allowClear={ false }
						onSelect={ handleMotionAudioSelect }
						options={ audioOptions }
						value={ localActionSettings.audio.motionEvent.audio }
					/>
					<Input
						hideCharMessage={ true }
						max={ 10 }
						min={ 1 }
						onUpdate={ handleMotionCountUpdate }
						type="number"
						value={ localActionSettings.audio.motionEvent.count }
					/>
				</div>
				<div className="form-row">
					<span>Tamper Event</span>
					<Select
						allowClear={ false }
						onSelect={ handleTamperAudioSelect }
						options={ audioOptions }
						value={ localActionSettings.audio.tamperEvent.audio }
					/>
					<Input
						hideCharMessage={ true }
						max={ 10 }
						min={ 1 }
						onUpdate={ handleTamperCountUpdate }
						type="number"
						value={ localActionSettings.audio.tamperEvent.count }
					/>
				</div>
				<div className="form-row">
					<span>AI Analytics</span>
					<Link to="/setup/camera/analytics">Go to AI Analytics to set Audio.</Link>
					<Input
						hideCharMessage={ true }
						max={ 10 }
						min={ 1 }
						onUpdate={ handleAiAnalyticsCountUpdate }
						type="number"
						value={ localActionSettings.audio.aiAnalytics }
					/>
				</div>
				<div className="schedule-title">
					<h4>Schedule</h4>
					<Popover content={ link }>
						<Icon name="circle-info" iconWeight={ IconWeight.REGULAR } size={ IconSize.SMALLER } />
					</Popover>
				</div>
				<div className="schedules">
					{ Object.entries(localActionSettings.audio.schedule).map(renderSchedule) }
				</div>
			</div>
		</div>
	);
}

export default memo(Audio);
